import { Box, FormControl, InputLabel, Paper, Stack, styled, TextField, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Grid, InputAdornment, OutlinedInput } from '@mui/material';
import { ChangeEvent, FC, FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMaxScreen } from '../../hooks';
import { DeleteButton } from '../delete-button';

interface Props {
  'data-testid'?: string;
  disabled?: boolean;
  invalidLabels?: [string, string][];
  labelColor?: string;
  labels: Record<string, string>;
  onChange: (labels: Record<string, string>) => Promise<void>;
  onValidationChange?: (hasErrors: boolean) => void;
  placeholder?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  textfieldLabel?: string;
}

interface StyledPaperProps {
  labelColor?: string;
}

const StyledPaper = styled(Paper)<StyledPaperProps>(({ labelColor }) => ({
  backgroundColor: labelColor,
}));

export const LabelsEditor: FC<Props> = ({
  disabled = false,
  invalidLabels,
  labelColor,
  labels: inputLabels,
  placeholder,
  onChange,
  onValidationChange,
  required,
  size,
  textfieldLabel,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const smallScreen = useMaxScreen('md');
  const [currentLabel, setCurrentLabel] = useState<string>('');
  const [labels, setLabels] = useState<Record<string, string>>(inputLabels);
  const [isLoading, setIsLoading] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const inputField = useRef<HTMLInputElement>(null);
  const labelWithAdornment = textfieldLabel && required ? textfieldLabel + ' *' : textfieldLabel;

  const updateLabels = (updatedLabels: Record<string, string>): void => {
    setIsLoading(true);
    onChange(updatedLabels)
      .then(() => {
        setIsLoading(false);
        setLabels(updatedLabels);
        setCurrentLabel('');
      })
      .catch(() => setIsLoading(false));
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement;
    if (e.key === 'Enter') {
      target.blur();
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const labelExists = Object.keys(labels).includes(e.target.value);
    setIsDuplicate(labelExists);
    setCurrentLabel(e.target.value);
  };

  const onLabelInputBlur = (e: FocusEvent<HTMLInputElement>): void => {
    const labelToSave = e.target.value.trim();
    if (labelToSave && !Object.keys(labels).includes(labelToSave)) {
      updateLabels({
        ...labels,
        [labelToSave]: '',
      });
    }
  };

  const onValueChange = (e: ChangeEvent<HTMLInputElement>, key: string): void => {
    setLabels({
      ...labels,
      [key]: e.target.value,
    });
  };

  const onValueInputBlur = (e: FocusEvent<HTMLInputElement>): void => {
    const valueExists = e.target.value.trim();
    if (valueExists) {
      updateLabels(labels);
    }
  };

  const removeLabel = (labelToRemove: string) => (): Promise<void> => {
    const updatedLabels = {
      ...labels,
    };
    delete updatedLabels[labelToRemove];

    updateLabels(updatedLabels);
    return Promise.resolve();
  };

  useEffect(() => {
    const emptyValuesExist = Object.values(labels).some((value) => value.trim() === '');
    onValidationChange?.(emptyValuesExist);
  }, [labels, onValidationChange]);

  useEffect(() => {
    setLabels(inputLabels);
  }, [inputLabels]);

  return (
    <Stack data-testid={props['data-testid']} direction='column' spacing={2}>
      {labels && Object.entries(labels).map(([key, value], index) => (
        <Grid key={key} container spacing={0} direction="row" alignItems='center' justifyContent='space-between'>
          <Grid item sm={4}>
            <Box m={1} py={.25}>
              <StyledPaper aria-label="Item Label" elevation={0} labelColor={labelColor}>
                {smallScreen
                  ?
                  <Typography variant="subtitle2" fontWeight="bold" data-testid={`label-key-${index}`} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
                    {key}
                  </Typography>
                  :
                  <Tooltip arrow followCursor placement="top" title={key}>
                    <Typography variant="subtitle2" fontWeight="bold" data-testid={`label-key-${index}`} overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
                      {key}
                    </Typography>
                  </Tooltip>
                }
              </StyledPaper>
            </Box>
          </Grid>
          <Grid item sm={8} sx={{ flex: '1 0 auto' }}>
            <FormControl fullWidth variant="outlined" size={size} disabled={disabled || isLoading}>
              <InputLabel error={(invalidLabels?.some(([match]) => key === match) || value.trim() === '')}>
                {labelWithAdornment}
              </InputLabel>
              <OutlinedInput
                data-testid={`label-value-${index}`}
                endAdornment={
                  <InputAdornment position="end">
                    <DeleteButton data-testid={`delete-button-${index}`} onClick={removeLabel(key)} disabled={disabled || isLoading} />
                  </InputAdornment>
                }
                error={(invalidLabels && invalidLabels.some(([match]) => key === match)) || value.trim() === ''}
                label={textfieldLabel}
                onBlur={onValueInputBlur}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onValueChange(e, key)}
                onKeyDown={onInputKeyDown}
                size={size}
                type="text"
                value={value}
              />
            </FormControl>
          </Grid>
        </Grid>
      ))}
      <TextField
        data-testid="label-input"
        disabled={disabled || isLoading}
        error={isDuplicate}
        label={placeholder ?? t('common:common.hint.label')}
        onBlur={onLabelInputBlur}
        onChange={onInputChange}
        onKeyDown={onInputKeyDown}
        ref={inputField}
        size={size}
        type="text"
        value={currentLabel}
        variant="outlined"
      />
    </Stack>
  );
};
